/**
 * BS4IT Cybersec Assessment — Result Page (público)
 *
 * Entrega instantânea: score, banda, pilares, quadrante, roadmap, CTAs.
 */
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import axios from 'axios'
import { Shield, TrendingUp, FileText, ChevronRight } from 'lucide-react'
import AssessmentHeader from '@/components/assessment/AssessmentHeader'
import BandHero from '@/components/assessment/BandHero'
import MetricCardGrid from '@/components/assessment/MetricCardGrid'
import PillarBars from '@/components/assessment/PillarBars'
import CtaSection from '@/components/assessment/CtaSection'
import MethodologyCitations from '@/components/assessment/MethodologyCitations'

type Pillar = {
  bruto: number
  max_bruto: number
  peso: number
  normalizado: number
}
type Scoring = {
  score_total: number
  banda: {
    id: string
    label: string
    color: string
    sla: string
    dimensao_soc: string
    min: number
    max: number
    forced_by_regulation?: boolean
  }
  pilares: Record<string, Pillar>
  quadrante: {
    key: string
    label: string
    pitch: string
    is_critical: boolean
    x: number
    y: number
    x_max: number
    y_max: number
  }
  roadmap: Array<{
    id: number
    title: string
    period: string
    subtitle: string
    items: string[]
  }>
  resumo_executivo: string
  cta: { label: string; subtitle: string; intensity: 'low' | 'medium' | 'high' }
  citacoes_norma: string[]
  citacoes_setor: string[]
  lead_score: number
  lead_tag: string
  setor: string | null
  porte: string | null
  urgencia: string | null
  disclaimer: string
}
type ResultData = {
  submission_id: string
  company_name: string
  contact_name: string
  submitted_at: string
  scoring: Scoring
}

const PILLAR_LABELS: Record<string, string> = {
  escala: 'Escala',
  heterogeneidade: 'Heterogeneidade técnica',
  criticidade: 'Criticidade e superfície',
  telemetria: 'Volume de telemetria',
  maturidade_gap: 'Maturidade SegInfo (gap)',
}
const PILLAR_COLORS: Record<string, string> = {
  escala: '#06b6d4',
  heterogeneidade: '#8b5cf6',
  criticidade: '#ef4444',
  telemetria: '#f59e0b',
  maturidade_gap: '#10b981',
}

export default function AssessmentBS4ITResultPage() {
  const { id } = useParams<{ id: string }>()
  const [data, setData] = useState<ResultData | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!id) return
    axios.get<ResultData>(`/api/v1/assessments/bs4it-cybersec/submissions/${id}/result`)
      .then(({ data }) => setData(data))
      .catch((err) => setError(err?.response?.data?.detail || 'Erro ao carregar resultado'))
  }, [id])

  if (error) {
    return <Center title="Erro" sub={error} />
  }
  if (!data) {
    return <Center title="Carregando resultado…" sub="" />
  }

  const { scoring, company_name } = data
  const { banda, quadrante, pilares, roadmap, cta, citacoes_norma, citacoes_setor } = scoring

  const pillarItems = Object.entries(pilares).map(([key, p]) => ({
    key,
    label: PILLAR_LABELS[key],
    value: p.normalizado,
    max: p.peso,
    color: PILLAR_COLORS[key],
    hint: `Peso ${p.peso}%`,
  }))

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <AssessmentHeader
        partners={[
          { src: 'https://nai-it.com.br/wp-content/uploads/2025/11/logo_nai_branco.png', alt: 'NAI', height: 40 },
          { src: 'https://social.myncc.com.br/landingpages/assets/bs4it-logo.png', alt: 'BS4IT', height: 40 },
        ]}
        sticky={false}
      />

      <main className="max-w-5xl mx-auto px-4 py-10 space-y-8 print:py-4">
        {/* Capa */}
        <div className="text-center">
          <p className="text-[10px] uppercase tracking-widest text-cyan-400 font-bold">
            Diagnóstico de Maturidade em Operações de Segurança
          </p>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight mt-2">
            {company_name}
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            ID #{data.submission_id.slice(0, 8)} · {new Date(data.submitted_at).toLocaleDateString('pt-BR')}
          </p>
        </div>

        {/* Hero com banda + score (componente compartilhado) */}
        <BandHero
          bandLabel={banda.label}
          bandColor={banda.color}
          bandSubtitle={banda.forced_by_regulation ? 'Forçada por setor altamente regulado' : undefined}
          score={scoring.score_total}
          scoreMax={100}
          scoreUnit="/100"
          resumoExecutivo={scoring.resumo_executivo}
          badgePrefix="Banda · "
        />

        {/* 3 metric cards (compartilhado) */}
        <MetricCardGrid cards={[
          { icon: Shield, label: 'SLA Recomendado', value: banda.sla, subtitle: banda.dimensao_soc, color: banda.color },
          { icon: TrendingUp, label: 'Quadrante decisório', value: quadrante.label,
            subtitle: quadrante.pitch, color: quadrante.is_critical ? '#ef4444' : banda.color },
          { icon: FileText, label: 'Lead Score (interno)', value: `${scoring.lead_score}`,
            subtitle: `Tag: ${scoring.lead_tag}`, color: '#3b82f6' },
        ]} />

        {/* Pilares (compartilhado) */}
        <PillarBars title="Pontuação por pilar" items={pillarItems} />

        {/* Quadrante visual (específico BS4IT) */}
        <section>
          <h2 className="text-xl font-bold mb-4">Mapa decisório · Complexidade × Maturidade</h2>
          <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4 sm:p-6">
            <QuadrantChart quadrante={quadrante} />
          </div>
        </section>

        {/* Roadmap (3 ondas — específico BS4IT) */}
        <section>
          <h2 className="text-xl font-bold mb-1">Roadmap recomendado · 3 ondas</h2>
          <p className="text-xs text-slate-400 mb-4">
            Itens priorizados por banda, ajustados ao seu contexto (OT, apps expostas, SIEM, setor regulado).
          </p>
          <div className="space-y-4">
            {roadmap.map((wave, i) => (
              <WaveCard key={wave.id} wave={wave} colorIndex={i} />
            ))}
          </div>
        </section>

        {/* Citações (compartilhado) */}
        <MethodologyCitations
          norms={citacoes_norma}
          sectorCitations={citacoes_setor}
          disclaimer={scoring.disclaimer}
        />

        {/* CTA (compartilhado) */}
        <CtaSection
          intensity={cta.intensity}
          title={cta.label}
          subtitle={cta.subtitle}
          whatsappMessage="Vim do Assessment BS4IT"
          refazerHref="/assessment/bs4it"
        />
      </main>

      <footer className="border-t border-slate-800 mt-12 py-6 text-center text-xs text-slate-500 print:hidden">
        Nai S/A × BS4IT · Juntas promovendo a blindagem lógica e regulatória da sua empresa.
      </footer>
    </div>
  )
}


function WaveCard({ wave, colorIndex }: {
  wave: { id: number; title: string; period: string; subtitle: string; items: string[] }
  colorIndex: number
}) {
  const colors = ['#06b6d4', '#8b5cf6', '#10b981']
  const color = colors[colorIndex] || '#06b6d4'
  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="inline-flex items-center gap-2">
            <span className="text-xl font-black" style={{ color }}>Onda {wave.id}</span>
            <span className="text-xs text-slate-400">·</span>
            <span className="text-sm font-semibold">{wave.title}</span>
          </div>
          <p className="text-[10px] uppercase tracking-wider text-slate-500 mt-1">
            {wave.period} · {wave.subtitle}
          </p>
        </div>
      </div>
      <ul className="space-y-1.5">
        {wave.items.map((item, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
            <ChevronRight className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color }} />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function QuadrantChart({ quadrante }: { quadrante: any }) {
  // 2x2 grid com ponto posicionado proporcionalmente
  const xPct = (quadrante.x / quadrante.x_max) * 100
  const yPctInverted = 100 - ((quadrante.y / quadrante.y_max) * 100)  // SVG y é invertido
  return (
    <div className="relative w-full aspect-square max-w-md mx-auto">
      <svg viewBox="0 0 100 100" className="w-full h-full">
        {/* 4 quadrantes */}
        <rect x="0" y="0" width="50" height="50" fill="#1e293b" opacity="0.4" />
        <rect x="50" y="0" width="50" height="50" fill="#06b6d4" opacity="0.15" />
        <rect x="0" y="50" width="50" height="50" fill="#f59e0b" opacity="0.15" />
        <rect x="50" y="50" width="50" height="50" fill="#ef4444" opacity="0.2" />
        {/* Grades */}
        <line x1="50" y1="0" x2="50" y2="100" stroke="#475569" strokeWidth="0.3" />
        <line x1="0" y1="50" x2="100" y2="50" stroke="#475569" strokeWidth="0.3" />
        {/* Labels dos quadrantes */}
        <text x="25" y="22" fill="#94a3b8" fontSize="3.5" textAnchor="middle" fontWeight="bold">Sustentar</text>
        <text x="75" y="22" fill="#22d3ee" fontSize="3.5" textAnchor="middle" fontWeight="bold">Otimizar</text>
        <text x="25" y="80" fill="#fbbf24" fontSize="3.5" textAnchor="middle" fontWeight="bold">Construir base</text>
        <text x="75" y="80" fill="#ef4444" fontSize="3.5" textAnchor="middle" fontWeight="bold">Zona crítica</text>
        {/* Ponto */}
        <circle cx={xPct} cy={yPctInverted} r="2.5" fill="#ffffff" stroke={quadrante.is_critical ? '#ef4444' : '#06b6d4'} strokeWidth="1" />
        <circle cx={xPct} cy={yPctInverted} r="5" fill="none" stroke={quadrante.is_critical ? '#ef4444' : '#06b6d4'} strokeWidth="0.5" opacity="0.5">
          <animate attributeName="r" from="5" to="8" dur="1.5s" repeatCount="indefinite" />
          <animate attributeName="opacity" from="0.5" to="0" dur="1.5s" repeatCount="indefinite" />
        </circle>
        {/* Eixos */}
        <text x="50" y="98" fill="#64748b" fontSize="2.5" textAnchor="middle">→ Complexidade técnica</text>
        <text x="2" y="50" fill="#64748b" fontSize="2.5" transform="rotate(-90 2 50)" textAnchor="middle">→ Maturidade</text>
      </svg>
      <p className="text-center text-xs text-slate-400 mt-3">
        Posição: <strong className="text-white">x={quadrante.x.toFixed(1)}</strong> (técnica) ·
        <strong className="text-white"> y={quadrante.y.toFixed(1)}</strong> (maturidade)
      </p>
    </div>
  )
}

function Center({ title, sub }: { title: string; sub: string }) {
  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center text-center px-4">
      <div>
        <h1 className="text-2xl font-bold text-white">{title}</h1>
        <p className="text-slate-400 mt-2">{sub}</p>
      </div>
    </div>
  )
}
